import { useCallback, useState } from 'react'

import { useQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { useAccountId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { VariablesOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import { logError } from '~publish/legacy/utils/logError'

export const GetCustomerIOAttributesQuery = graphql(`
  query getCustomerIOAttributesQuery($input: GetCustomerIOAttributesInput!) {
    getCustomerIOAttributes(input: $input) {
      __typename
      attributes {
        postsDaily
        commentsDaily
        mentionsDaily
        commentsHourly
        mentionsHourly
        communityPostsDaily
        communityCommentsDaily
        communityMentionsDaily
        unsubscribed
      }
    }
  }
`)

export const UpdateCustomerIOAttributesMutation = graphql(`
  mutation updateCustomerIOAttributesMutation(
    $input: UpdateCustomerIOAttributesInput!
  ) {
    updateCustomerIOAttributes(input: $input) {
      __typename
      ... on UpdateCustomerIOAttributesSuccess {
        _empty
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type NotificationOptionProps = {
  id: string
  subscribed: boolean
}

/**
 * The Customer.io attributes that say what the Daily Post Recap covers. The
 * ids double as the `CustomerIOAttributesInput` field names, so a caller
 * naming a topic is enough to address the right attribute.
 */
const DAILY_RECAP_TOPIC_IDS = [
  'postsDaily',
  'commentsDaily',
  'mentionsDaily',
] as const

/** The Customer.io attributes that say what the Hourly Comment Alerts cover. */
const HOURLY_ALERT_TOPIC_IDS = ['commentsHourly', 'mentionsHourly'] as const

/** The Customer.io attributes that say what the Daily Community Recap covers. */
const DAILY_COMMUNITY_RECAP_TOPIC_IDS = [
  'communityPostsDaily',
  'communityCommentsDaily',
  'communityMentionsDaily',
] as const

const TOPIC_IDS: ReadonlySet<string> = new Set([
  ...DAILY_RECAP_TOPIC_IDS,
  ...HOURLY_ALERT_TOPIC_IDS,
  ...DAILY_COMMUNITY_RECAP_TOPIC_IDS,
])

type CustomerIOAttributesInput = NonNullable<
  VariablesOf<typeof UpdateCustomerIOAttributesMutation>['input']['attributes']
>

export type DailyRecapTopicId = (typeof DAILY_RECAP_TOPIC_IDS)[number]

export type DailyRecapTopics = Record<DailyRecapTopicId, boolean>

export type HourlyAlertTopicId = (typeof HOURLY_ALERT_TOPIC_IDS)[number]

export type HourlyAlertTopics = Record<HourlyAlertTopicId, boolean>

export type DailyCommunityRecapTopicId =
  (typeof DAILY_COMMUNITY_RECAP_TOPIC_IDS)[number]

export type DailyCommunityRecapTopics = Record<
  DailyCommunityRecapTopicId,
  boolean
>

export type TopicId =
  | DailyRecapTopicId
  | HourlyAlertTopicId
  | DailyCommunityRecapTopicId

function isTopicId(id: string): id is TopicId {
  return TOPIC_IDS.has(id)
}

/**
 * Customer.io hands booleans back as real booleans or as their stringified
 * form depending on how the attribute was written, so every read normalizes.
 */
function normalizeToBoolean(value: unknown): boolean {
  return typeof value === 'string' ? value === 'true' : Boolean(value)
}

export const useCustomerIOPreferences = (): {
  dailyRecapTopics: DailyRecapTopics
  hourlyAlertTopics: HourlyAlertTopics
  dailyCommunityRecapTopics: DailyCommunityRecapTopics
  updateNotificationSetting: (
    options: NotificationOptionProps,
  ) => Promise<boolean>
  isUnsubscribedFromAllEmails: boolean
  updateGlobalUnsubscribe: (unsubscribed: boolean) => Promise<boolean>
  loading: boolean
  error: Error | undefined
} => {
  const accountId = useAccountId()

  // Optimistic override for the global unsubscribe state, applied immediately
  // in updateGlobalUnsubscribe so the topic toggles + warning react in-session.
  const [optimisticUnsubscribed, setOptimisticUnsubscribed] = useState<
    boolean | null
  >(null)

  // Topic values the server accepted this session. The query is never
  // refetched after a write, so these win over the first fetch.
  const [confirmedTopics, setConfirmedTopics] = useState<
    Partial<Record<TopicId, boolean>>
  >({})
  // Topic values written but not yet confirmed, shown straight away and
  // dropped once the write settles: a failure falls back to the stored value.
  const [pendingTopics, setPendingTopics] = useState<
    Partial<Record<TopicId, boolean>>
  >({})

  const { data, loading, error } = useQuery(GetCustomerIOAttributesQuery, {
    variables: {
      input: {
        accountId: accountId ?? '',
      },
    },
    skip: !accountId,
  })

  const [updateCustomerIOAttributes] = useTypedMutation(
    UpdateCustomerIOAttributesMutation,
    (customerIOMutationData) =>
      customerIOMutationData.updateCustomerIOAttributes,
    {
      successTypename: 'UpdateCustomerIOAttributesSuccess',
    },
  )

  const updateNotificationSetting = useCallback(
    async ({ id, subscribed }: NotificationOptionProps): Promise<boolean> => {
      if (!accountId) return false

      if (!isTopicId(id)) {
        logError(new Error(`Unknown Customer.io topic: ${id}`), {
          context: 'updateCustomerIOPreferences',
        })
        return false
      }

      setPendingTopics((topics) => ({ ...topics, [id]: subscribed }))

      const result = await updateCustomerIOAttributes({
        variables: {
          input: {
            accountId,
            // Each topic id doubles as its `CustomerIOAttributesInput` field
            // name, so the id addresses the attribute on its own.
            attributes: { [id]: subscribed } as CustomerIOAttributesInput,
          },
        },
      })

      // A later write to the same topic owns its pending value now.
      setPendingTopics((topics) => {
        if (topics[id] !== subscribed) return topics
        const next = { ...topics }
        delete next[id]
        return next
      })

      if (result.success) {
        setConfirmedTopics((topics) => ({ ...topics, [id]: subscribed }))
        return true
      }

      logError(result.error, { context: 'updateCustomerIOPreferences' })
      toast.error(
        result.error.message ||
          'Sorry! There was an issue updating your notification preferences. Please try again.',
      )
      return false
    },
    [accountId, updateCustomerIOAttributes],
  )

  const updateGlobalUnsubscribe = useCallback(
    async (unsubscribed: boolean): Promise<boolean> => {
      if (!accountId) return false

      // The success payload carries no attributes and Customer.io's attribute
      // read lags the write, so a refetch would race. Optimistically reflect
      // the new state (it drives the topic toggles + warning) and revert only
      // if the mutation fails.
      setOptimisticUnsubscribed(unsubscribed)

      const result = await updateCustomerIOAttributes({
        variables: {
          input: {
            accountId,
            attributes: { unsubscribed },
          },
        },
      })

      if (result.success) {
        return true
      }

      setOptimisticUnsubscribed(null)
      logError(result.error, { context: 'unsubscribeCustomerIOPreferences' })
      toast.error(
        result.error.message ||
          'Sorry! There was an issue updating your notification preferences. Please try again.',
      )
      return false
    },
    [accountId, updateCustomerIOAttributes],
  )

  const attributes = data?.getCustomerIOAttributes?.attributes
  const topicValue = (id: TopicId): boolean =>
    pendingTopics[id] ??
    confirmedTopics[id] ??
    normalizeToBoolean(attributes?.[id])
  const dailyRecapTopics: DailyRecapTopics = {
    commentsDaily: topicValue('commentsDaily'),
    mentionsDaily: topicValue('mentionsDaily'),
    postsDaily: topicValue('postsDaily'),
  }
  const hourlyAlertTopics: HourlyAlertTopics = {
    commentsHourly: topicValue('commentsHourly'),
    mentionsHourly: topicValue('mentionsHourly'),
  }
  const dailyCommunityRecapTopics: DailyCommunityRecapTopics = {
    communityPostsDaily: topicValue('communityPostsDaily'),
    communityCommentsDaily: topicValue('communityCommentsDaily'),
    communityMentionsDaily: topicValue('communityMentionsDaily'),
  }

  const isUnsubscribedFromAllEmails =
    optimisticUnsubscribed ??
    Boolean(data?.getCustomerIOAttributes?.attributes?.unsubscribed)

  return {
    dailyRecapTopics,
    hourlyAlertTopics,
    dailyCommunityRecapTopics,
    updateNotificationSetting,
    isUnsubscribedFromAllEmails,
    updateGlobalUnsubscribe,
    loading,
    error,
  }
}
